// frontend/src/super-admin/site/SeoSettingsPage.tsx
//
// Default meta tags + robots + analytics ids for the marketing site.

import * as React from 'react';
import { toast } from 'sonner';
import { Search, Lock, Activity } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { PageHeader } from '../../design-system/components/PageHeader';
import { LoadingState } from '../../design-system/components/LoadingState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { ImageUploadInput } from '../../design-system/components/ImageUploadInput';
import { VISUAL } from '../../design-system/visual';
import { seoApi, type SeoSettings, type SeoSettingsInput } from '../../modules/site-settings/api';
import { useAdminPermission } from '../../core/auth/useAdminPermission';

export function SeoSettingsPage() {
  const canWrite = useAdminPermission('site.seo.write');
  const [seo, setSeo] = React.useState<SeoSettings | null>(null);
  const [draft, setDraft] = React.useState<SeoSettingsInput>({});
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  function load() {
    setError(null);
    setSeo(null);
    seoApi.get()
      .then((r) => {
        setSeo(r);
        setDraft({
          default_title: r.default_title,
          default_description: r.default_description,
          default_og_image_url: r.default_og_image_url ?? '',
          default_twitter_handle: r.default_twitter_handle ?? '',
          default_robots: r.default_robots,
          sitemap_enabled: r.sitemap_enabled,
          indexable: r.indexable,
          gtm_id: r.gtm_id ?? '',
          ga4_id: r.ga4_id ?? '',
        });
      })
      .catch((e) => setError(String(e.message ?? e)));
  }

  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await seoApi.update(draft);
      setSeo(r);
      toast.success('SEO settings saved');
    } catch (err: any) {
      toast.error('Save failed', { description: err.message ?? String(err) });
    } finally { setBusy(false); }
  }

  if (error) return <ErrorState description={error} onRetry={load} />;
  if (!seo) return <LoadingState variant="page" />;

  return (
    <div>
      <PageHeader
        eyebrow="Site / Configuration"
        title="SEO & analytics"
        description="Default meta tags, robots policy, sitemap, and analytics ids. Per-post SEO can override the title and description."
      />

      <form onSubmit={save} className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl space-y-6">
        {/* Defaults */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7 space-y-5'].join(' ')}>
          <div className="flex items-center gap-3 mb-1">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/15 ring-1 ring-coral/25">
              <Search className="h-4 w-4 text-coral" strokeWidth={1.7} />
            </div>
            <h3 className={VISUAL.dashSection}>Default meta tags</h3>
          </div>

          <div className="grid gap-1.5">
            <Label>Default title</Label>
            <Input
              value={draft.default_title ?? ''}
              onChange={(e) => setDraft({ ...draft, default_title: e.target.value })}
              disabled={!canWrite}
              maxLength={70}
            />
            <p className="text-[12px] text-muted-foreground">{(draft.default_title ?? '').length}/70 characters</p>
          </div>

          <div className="grid gap-1.5">
            <Label>Default description</Label>
            <Textarea
              value={draft.default_description ?? ''}
              onChange={(e) => setDraft({ ...draft, default_description: e.target.value })}
              rows={2}
              disabled={!canWrite}
              maxLength={160}
            />
            <p className="text-[12px] text-muted-foreground">{(draft.default_description ?? '').length}/160 characters</p>
          </div>

          <ImageUploadInput
            value={draft.default_og_image_url ?? ''}
            onChange={(url) => setDraft((d) => ({ ...d, default_og_image_url: url }))}
            endpoint="super-admin"
            area="seo"
            label="Default OG image"
            disabled={!canWrite}
          />

          <div className="grid gap-1.5">
            <Label>Twitter handle</Label>
            <Input
              value={draft.default_twitter_handle ?? ''}
              onChange={(e) => setDraft({ ...draft, default_twitter_handle: e.target.value })}
              disabled={!canWrite}
              placeholder="@worqly"
            />
          </div>
        </section>

        {/* Indexing */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7 space-y-5'].join(' ')}>
          <div className="flex items-center gap-3 mb-1">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-lavender-100 ring-1 ring-lavender-200">
              <Lock className="h-4 w-4 text-lavender-700" strokeWidth={1.7} />
            </div>
            <h3 className={VISUAL.dashSection}>Indexing & robots</h3>
          </div>

          <div className="grid gap-1.5">
            <Label>Robots directive</Label>
            <Input
              value={draft.default_robots ?? ''}
              onChange={(e) => setDraft({ ...draft, default_robots: e.target.value })}
              disabled={!canWrite}
              placeholder="index, follow"
            />
          </div>

          <Toggle
            label="Indexable"
            description="Allow search engines to crawl the marketing site. Disable for staging."
            value={!!draft.indexable}
            onToggle={() => canWrite && setDraft({ ...draft, indexable: !draft.indexable })}
            disabled={!canWrite}
          />

          <Toggle
            label="Sitemap enabled"
            description="Serve /sitemap.xml from the marketing host."
            value={!!draft.sitemap_enabled}
            onToggle={() => canWrite && setDraft({ ...draft, sitemap_enabled: !draft.sitemap_enabled })}
            disabled={!canWrite}
          />
        </section>

        {/* Analytics */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7 space-y-5'].join(' ')}>
          <div className="flex items-center gap-3 mb-1">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/15 ring-1 ring-coral/25">
              <Activity className="h-4 w-4 text-coral" strokeWidth={1.7} />
            </div>
            <h3 className={VISUAL.dashSection}>Analytics</h3>
          </div>

          <div className="grid gap-1.5">
            <Label>Google Tag Manager ID</Label>
            <Input
              value={draft.gtm_id ?? ''}
              onChange={(e) => setDraft({ ...draft, gtm_id: e.target.value })}
              disabled={!canWrite}
              placeholder="GTM-XXXXXX"
            />
          </div>

          <div className="grid gap-1.5">
            <Label>GA4 Measurement ID</Label>
            <Input
              value={draft.ga4_id ?? ''}
              onChange={(e) => setDraft({ ...draft, ga4_id: e.target.value })}
              disabled={!canWrite}
              placeholder="G-XXXXXXXX"
            />
          </div>
        </section>

        {canWrite && (
          <div className="flex items-center justify-between">
            <p className="text-[12.5px] text-muted-foreground">
              Last updated <span className="text-foreground font-semibold">{new Date(seo.updated_at).toLocaleString()}</span>
            </p>
            <Button type="submit" disabled={busy} className="h-11 px-6 rounded-full text-[14px] font-semibold">
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

function Toggle({
  label, description, value, onToggle, disabled,
}: {
  label: string;
  description: string;
  value: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-foreground">{label}</p>
        <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={onToggle}
        disabled={disabled}
        className={
          'shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ' +
          (value ? 'bg-coral' : 'bg-muted ring-1 ring-border/40')
        }
      >
        <span
          className={
            'inline-block h-5 w-5 rounded-full bg-white shadow-pill transition-transform ' +
            (value ? 'translate-x-5' : 'translate-x-0.5')
          }
        />
      </button>
    </div>
  );
}
